// Preferências da tela de atendimentos, por usuário.
//
// A pessoa já escolhe aba, ordem e filtros toda vez que abre a tela; o que
// faltava era lembrar. Ficam no usuário (coluna `uiPreferences`), não no
// navegador: assim seguem quem trabalha em mais de uma máquina.
//
// Fora do cookie de propósito — ele viaja em toda requisição à API e vale só
// para o que precisa pintar antes da primeira resposta (o layout, o tema).
// Aqui nada precisa: a tela só monta com a sessão já restaurada.
import api from "./api";
import toastError from "../errors/toastError";
import { ITENS_NOVOS } from "../components/TicketListItem/itensDoCartao";
import { COLUNAS_NOVAS } from "../components/TicketListItem/colunasPainel";
import { BOTOES_NOVOS_DA_LINHA } from "../components/TicketListItem/botoesDaLinha";
import { FERRAMENTAS_NOVAS } from "../components/TicketsManager/ferramentasDaLista";
import { BOTOES_NOVOS } from "../components/Ticket/botoesDaConversa";

let prefs = {};

export const hidratarPrefsTickets = usuario => {
	prefs = usuario?.uiPreferences || {};
};

// O perfil de tela do CARGO (`/permissions/mine`). É regra, não preferência:
// diz o que a tela mostra por padrão para quem tem aquele cargo e quais itens
// o atendente não pode mexer.
//
// Nulo — o estado de todo cargo que o admin não configurou — devolve o
// comportamento de sempre: cada um monta a própria tela.
let perfilDoCargo = null;

export const EVENTO_PERFIL_DO_CARGO = "perfil-do-cargo:mudou";

export const hidratarPerfilDoCargo = perfil => {
	perfilDoCargo = perfil && typeof perfil === "object" ? perfil : null;

	// Quem já está montado leu a preferência antes de a resposta chegar; sem o
	// aviso, a barra ficaria com o padrão de fábrica até o F5.
	window.dispatchEvent(new CustomEvent(EVENTO_PERFIL_DO_CARGO));
};

// Os itens que o cargo NÃO deixa o atendente mexer. A tela de personalização
// usa isto para desabilitar o controle e dizer por quê.
export const itensTravadosDe = chave => perfilDoCargo?.[chave]?.fixos || [];

export const cargoTrava = chave => itensTravadosDe(chave).length > 0;

// Preferência de valor único que o cargo fixou (`{ padrao, fixo: true }`).
export const valorFixoPeloCargo = chave => !!perfilDoCargo?.[chave]?.fixo;

// A escolha que vale, item a item.
//
// Fixo do cargo manda: dentro do padrão do cargo fica sempre visível, fora
// dele fica sempre escondido — e a regra é aplicada na LEITURA, então nem uma
// preferência gravada por fora da tela contorna a trava.
// O que não é fixo segue a preferência do usuário e, na falta dela, o padrão
// do cargo; sem cargo configurado, o padrão de fábrica de sempre.
const comRegraDoCargo = (chave, escolhaDoUsuario, padraoDeFabrica) => {
	const regra = perfilDoCargo?.[chave];

	if (!regra) return escolhaDoUsuario;
	// Valor único (navegacaoLista, escopoBuscaMensagem): fixo vale o do cargo.
	if (regra.fixo) return regra.padrao;

	const padraoDoCargo = regra.padrao || padraoDeFabrica;
	const fixos = regra.fixos || [];

	if (!Array.isArray(escolhaDoUsuario)) return escolhaDoUsuario;
	if (!fixos.length) return escolhaDoUsuario;

	const livres = escolhaDoUsuario.filter(id => !fixos.includes(id));
	const fixosVisiveis = fixos.filter(id => padraoDoCargo.includes(id));
	const resultado = [...livres, ...fixosVisiveis];

	// Mesmo conteúdo devolve o MESMO array: um array novo a cada leitura faria
	// o usePrefTickets regravar, o evento reler e o laço nunca parar.
	return resultado.length === escolhaDoUsuario.length &&
		resultado.every((id, i) => id === escolhaDoUsuario[i])
		? escolhaDoUsuario
		: resultado;
};

// O catálogo cresce: quem já personalizou uma lista de "o que aparece" tem a
// escolha gravada, e um item criado depois não está nela — ficaria escondido
// sem que ninguém tivesse escolhido escondê-lo. Ele entra só para quem nunca
// viu aquele id: gravar a lista (a pessoa mexeu nela, com o catálogo todo à
// vista) marca em `novosVistos` os ids desta chave, e daí em diante o que
// ficou de fora foi escolha.
const NOVOS = {
	itensDoCartao: ITENS_NOVOS,
	colunasPainel: COLUNAS_NOVAS,
	botoesDaLinha: BOTOES_NOVOS_DA_LINHA,
	ferramentasDaLista: FERRAMENTAS_NOVAS,
	botoesDaConversa: BOTOES_NOVOS,
};
const vistosDe = chave => (NOVOS[chave] || []).map(id => `${chave}.${id}`);

const comNovos = (chave, salvo) => {
	if (!NOVOS[chave] || !Array.isArray(salvo)) return salvo;

	const vistos = prefs.novosVistos || [];
	const faltando = NOVOS[chave].filter(
		id => !salvo.includes(id) && !vistos.includes(`${chave}.${id}`)
	);

	if (!faltando.length) return salvo;

	// Só na memória: abrir a tela não grava nada. Guardado em `prefs` para a
	// próxima leitura devolver o MESMO array (array novo a cada leitura vira
	// gravação em laço no usePrefTickets).
	prefs[chave] = [...salvo, ...faltando];
	return prefs[chave];
};

export const lerPrefTickets = (chave, padrao) => {
	const regra = perfilDoCargo?.[chave];
	const salvo =
		prefs[chave] === undefined
			? // Nunca personalizou: vale o padrão do cargo, se houver.
				(regra?.padrao ?? padrao)
			: comNovos(chave, prefs[chave]);

	return comRegraDoCargo(chave, salvo, padrao);
};

// Uma pessoa mexendo nos filtros mexe em vários de uma vez. Sem juntar, cada
// clique viraria uma requisição; com o agrupamento, vira uma só.
let pendentes = {};
let temporizador;

// Duas telas podem estar montadas lendo a MESMA preferência (o menu do
// atendimento muda os atalhos; a barra e o cabeçalho os mostram). Cada
// `usePrefTickets` tem o seu próprio estado, então quem muda avisa os outros —
// o `storage` do navegador não serve: ele só dispara para OUTRAS abas.
export const EVENTO_PREFS_TICKETS = "prefs-tickets:mudou";

export const gravarPrefTickets = (chave, valor) => {
	prefs[chave] = valor;
	pendentes[chave] = valor;

	const vistos = prefs.novosVistos || [];
	const maisVistos = vistosDe(chave).filter(v => !vistos.includes(v));
	if (maisVistos.length) {
		prefs.novosVistos = [...vistos, ...maisVistos];
		pendentes.novosVistos = prefs.novosVistos;
	}

	window.dispatchEvent(
		new CustomEvent(EVENTO_PREFS_TICKETS, { detail: { chave } })
	);

	clearTimeout(temporizador);
	temporizador = setTimeout(() => {
		const corpo = pendentes;
		pendentes = {};
		// A tela já obedeceu; falhar só não leva a escolha para o F5 e para a
		// próxima máquina — mas a pessoa precisa saber disso.
		api.put("/users/me/ui-preferences", corpo).catch(toastError);
	}, 800);
};

// Voltar tudo ao padrão. É o botão de saída de quem se enroscou num filtro
// salvo — sem ele, lista vazia por filtro esquecido não tem desfazer.
//
// Manda os padrões em vez de apagar a coluna porque a gravação é uma mescla:
// o que não vai no corpo continua lá.
export const PADRAO_TICKETS = {
	aba: "minhas",
	painel: "abertos",
	ordemAZ: false,
	soFavoritas: false,
	soAdiados: false,
	periodoResolvidos: "7",
	tamanhoFonteChat: "normal",
	filtroTags: [],
	filtroUsuarios: [],
	filtroConexoes: [],
	filas: [],
	visoes: [],
	acoesBarra: ["ligar", "notas", "copiloto", "arquivos"],
	ladoDaLista: "esquerda",
	painelContatoAberto: false,
	texturaDoChat: true,
	etiquetasNoCabecalho: true,
	avisoPausaPiscar: true,
	avisoPausaSom: false,
	// `null` = usa o volume padrão da empresa. Ver `useSomDeNotificacao`.
	volumeNotificacao: null,
	avisoPausaNotificacao: false,
	itensDoCartao: [
		"avatar",
		"previa",
		"seloCanal",
		"seloConexao",
		"seloFila",
		"seloAtendente",
		"etiquetas",
		"espera",
		"faixaDaFila",
	],
	botoesDaLinha: [
		"espiar",
		"aceitar",
		"transferir",
		"resolver",
		"ignorar",
		"reabrir",
		"favoritar",
	],
	botoesDaConversa: [
		"filtrar",
		"selecionar",
		"favoritas",
		"buscar",
		"emoji",
		"anexar",
		"biblioteca",
		"assinar",
		"iaCorrigir",
		"audio",
	],
	ferramentasDaLista: [
		"todos",
		"novo",
		"favoritas",
		"adiados",
		"resolverTodos",
		"ordem",
		"visoes",
	],
	colunasPainel: ["ultimaMensagem", "fila", "atendente"],
	// Estrutura da lista. Os padrões são a tela de sempre: abas, o par
	// Abertos/Fechados, toda ferramenta na barra e cartão de várias linhas.
	navegacaoLista: "abas",
	resolvidosComoAba: false,
	ferramentasNoMenu: [],
	densidadeLinha: "confortavel",
	// Busca nas mensagens: campo sempre à vista ou atrás de um ícone, e se
	// procura só na aba ou em todas.
	campoBuscaMensagem: "campo",
	escopoBuscaMensagem: "aba",
	larguraListaClassico: 340,
	larguraConversaFoco: 1100,
	larguraPainelContato: 320,
	assinar: true,
	// Restaurar esquece também quais itens novos a pessoa já viu: o padrão de
	// fábrica volta com eles à vista.
	novosVistos: [],
};

export const restaurarPrefsTickets = async () => {
	prefs = { ...PADRAO_TICKETS };
	// Uma gravação pendente do estado antigo desfaria a restauração.
	clearTimeout(temporizador);
	pendentes = {};

	// Avisa quem está na tela. Sem isto, restaurar a partir de Configurações —
	// onde as próprias opções estão à vista — só aparecia depois de recarregar.
	Object.keys(PADRAO_TICKETS).forEach(chave =>
		window.dispatchEvent(
			new CustomEvent(EVENTO_PREFS_TICKETS, { detail: { chave } })
		)
	);

	await api.put("/users/me/ui-preferences", PADRAO_TICKETS);
};
